import { useCelloReferrer } from '@hooks/cello/use-cello-referrer';
import {
  detectReferralSurface,
  trackReferralLandingContinueClicked,
  trackReferralLandingViewed,
} from '@modules/analytics/referral-program-events';
import {
  CELLO_REFERRAL_DISCOUNT_PERCENT,
  clearCachedCelloReferralCode,
  getFirstName,
} from '@modules/cello-referral';
import { useEffect, useRef } from 'react';

import * as styles from './CelloReferralLanding.css';
import { ClaimCallout } from './ClaimCallout';
import { Faqs } from './Faqs';
import { IntroSection } from './IntroSection';
import { LandingFooter } from './LandingFooter';
import { LandingNav } from './LandingNav';
import { ProductCards } from './ProductCards';
import { ReferralHero } from './ReferralHero';
import { Reviews } from './Reviews';
import { type ReferralOffer, buildCtaLabel } from './referral-copy';
import { StickyClaimBar } from './StickyClaimBar';
import { TrustBar } from './TrustBar';

export const CLAIM_HREF = '/login';

interface CelloReferralLandingProps {
  referralCode: string | null;
  referrerName: string | null;
}

function resolveOffer(
  referralCode: string | null,
  referrer: ReturnType<typeof useCelloReferrer>,
): ReferralOffer {
  if (!referralCode) {
    return 'unavailable';
  }
  if (referrer.loading) {
    return 'loading';
  }
  if (referrer.isValid === true) {
    return 'valid';
  }
  return referrer.isValid === false ? 'invalid' : 'unavailable';
}

export const CelloReferralLanding = ({ referralCode, referrerName }: CelloReferralLandingProps) => {
  const referrer = useCelloReferrer(referralCode);
  const offer = resolveOffer(referralCode, referrer);
  const heroCtaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (referrer.isValid === false) {
      clearCachedCelloReferralCode();
    }
  }, [referrer.isValid]);
  const firstName = referrer.firstName ?? getFirstName(referrerName);
  const ctaLabel = buildCtaLabel(offer, CELLO_REFERRAL_DISCOUNT_PERCENT);

  useEffect(() => {
    trackReferralLandingViewed({ surface: detectReferralSurface(), referralCode });
  }, [referralCode]);

  const handleClaimClick = () => trackReferralLandingContinueClicked(detectReferralSurface());

  return (
    <div className={styles.root}>
      <LandingNav claimHref={CLAIM_HREF} ctaLabel={ctaLabel} onClaimClick={handleClaimClick} />
      <main>
        <ReferralHero
          ctaRef={heroCtaRef}
          offer={offer}
          firstName={firstName}
          imageUrl={referrer.imageUrl}
          discountPercent={CELLO_REFERRAL_DISCOUNT_PERCENT}
          claimHref={CLAIM_HREF}
          onClaimClick={handleClaimClick}
        />
        <TrustBar />
        <IntroSection />
        <ProductCards claimHref={CLAIM_HREF} onClaimClick={handleClaimClick} />
        <Reviews />
        <Faqs />
        <ClaimCallout claimHref={CLAIM_HREF} ctaLabel={ctaLabel} onClaimClick={handleClaimClick} />
      </main>
      <LandingFooter claimHref={CLAIM_HREF} ctaLabel={ctaLabel} onClaimClick={handleClaimClick} />
      <StickyClaimBar
        watchRef={heroCtaRef}
        claimHref={CLAIM_HREF}
        ctaLabel={ctaLabel}
        onClaimClick={handleClaimClick}
      />
    </div>
  );
};
